import React, { CSSProperties, useContext } from 'react';
import { Chip, useTheme } from '@mui/material';
import styled from '@emotion/styled';
import { selectTaskTypes } from '@/redux/selectors/constantsSelectors';
import { convertStylesToStyled } from '@/_utils/utils';
import { ConfigContext } from '@/contextAPI/configContext';
import { useAppSelector } from '@/redux/hooks';

type TaskTypeProps = {
  type: string | 'blue' | 'orange' | 'lavender' | 'purple' | 'magenta' | 'teal' | 'green' | 'gray';
  prettyName: string;
};

const TaskType = ({ type, prettyName }: TaskTypeProps) => {
  const taskTypes = useAppSelector(selectTaskTypes);
  const theme = useTheme();
  const taskTypeLabelColor = useContext(ConfigContext)?.taskTypeLabelColor;

  if (!type) return null;
  let normalizedType = type?.toLowerCase();
  if (taskTypeLabelColor) normalizedType = taskTypeLabelColor;
  else if (normalizedType.includes('qc') || normalizedType.includes('q_c')) normalizedType = 'qc';
  else if (normalizedType.includes('compliance')) normalizedType = 'compliance';
  else if (normalizedType.includes('awaiting')) normalizedType = 'awaiting';
  else if (normalizedType.includes('review')) normalizedType = 'review';
  else if (normalizedType.includes('assign')) normalizedType = 'asset_workorder';
  else if (normalizedType.includes('create')) normalizedType = 'update_metadata';
  else if (normalizedType.includes('packaging')) normalizedType = 'packaging';
  else if (normalizedType.includes('screening')) normalizedType = 'screening';
  else if (normalizedType.includes('edit')) normalizedType = 'edit';
  else if (normalizedType.includes('ingest')) normalizedType = 'ingest';

  const getFormattingByType = (): CSSProperties => {
    switch (normalizedType) {
      case 'in_approval':
      case 'awaiting':
        // Yellow Chip Styles
        return {
          backgroundColor: theme.palette.warning.main + '3a',
          border: theme.palette.mode === 'dark' ? 'none' : `1.5px solid ${theme.palette.warning.main + '4a'}`,
          color: theme.palette.warning.main
        };
      // DELIVERY Task Types & Styling
      case 'asset_workorder':
      case 'blue':
        // Blue Chip Styles
        return {
          backgroundColor: '#3F87EB2a',
          border: theme.palette.mode === 'dark' ? 'none' : `1.5px solid #3F87EB`,
          color: '#3F87EB'
        };
      // EDIT Task Types & Styling
      case 'edit':
      case 'ingest':
      case 'orange':
        // Orange Chip Styles
        return {
          backgroundColor: '#FF7D032a',
          border: theme.palette.mode === 'dark' ? 'none' : `1.5px solid #FF7D03`,
          color: '#FF7D03'
        };
      case 'segmentation':
      case 'lavender':
        return {
          color: theme.palette.lavender.main,
          border: theme.palette.mode === 'dark' ? 'none' : `1.5px solid ${theme.palette.lavender.main + '8a'}`,
          backgroundColor: theme.palette.lavender.main + '2a'
        };
      case 'purple':
      case 'in_review':
      case 'review':
      case 'qc':
        return {
          backgroundColor: '#8855ee2a',
          border: theme.palette.mode === 'dark' ? 'none' : `1.5px solid #8855ee`,
          color: '#9764ff'
        };
      case 'magenta':
      case 'screening':
      case 'update_metadata':
        return {
          color: '#C73DDD',
          border: theme.palette.mode === 'dark' ? 'none' : `1.5px solid #C73DDD7a`,
          backgroundColor: '#C73DDD2a'
        };
      case 'compliance':
      case 'teal':
      case 'packaging':
        return {
          color: '#009FA8',
          border: theme.palette.mode === 'dark' ? 'none' : `1.5px solid #009FA8`,
          backgroundColor: '#009FA82a'
        };
      case 'accurate_player_transform_task':
      case 'green':
        return {
          backgroundColor: '#2AA4362a',
          color: '#2AA436',
          border: theme.palette.mode === 'dark' ? 'none' : `1.5px solid #2AA436`
        };
      // DEFAULT Task Types & Styling
      default:
        // Gray Chip
        return {
          backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[300] + '2a' : theme.palette.grey[700] + '2a',
          border: theme.palette.mode === 'dark' ? 'none' : `1.5px solid ${theme.palette.grey[800] + '6a'}`,
          color: theme.palette.mode === 'dark' ? theme.palette.grey[300] : theme.palette.grey[800]
        };
    }
  };

  return <TaskTypeChip label={prettyName || taskTypes[type]} formatting={getFormattingByType()} variant="filled" size="small" />;
};

export default TaskType;

const TaskTypeChip = styled(Chip)<{ formatting: CSSProperties }>`
  &.MuiChip-root {
    text-transform: uppercase;
    border-radius: 3px;
    max-width: 150px;
    line-height: 1.1;
    height: auto;
    white-space: normal;
    text-align: center;

    & .MuiChip-label {
      padding: ${(props) => props.theme.spacing(1)};
      color: ${(props) => props.theme.palette.text.primary};
      font-size: 0.8rem;
      white-space: normal;
    }

    ${({ formatting }) => convertStylesToStyled(formatting)}

    & .MuiChip-label {
      color: ${({ formatting }) => formatting.color};
    }
  }
`;
